import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getProducts, createOrder, getWorkersByShop, uploadOrderImage, getFactories,
} from '../services/api';
import { fileToResizedDataUrl, dataUrlBytes, prettyBytes, MAX_IMAGES } from '../utils/image';
import { format, addHours } from 'date-fns';
import toast from 'react-hot-toast';

// Greek-friendly normalize for search
const normalize = (str) => (str || '').toString().toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '');

// Format a Date for <input type="datetime-local"> (yyyy-MM-dd'T'HH:mm)
const formatLocal = (d) => format(d, "yyyy-MM-dd'T'HH:mm");

// Products with no category are gathered under this heading. It is not a
// row in the database — category_id is simply NULL.
const UNCAT_KEY = 'none';
const UNCAT_NAME = 'Χωρίς κατηγορία';

// The cart is keyed by product AND factory, because the same product may be
// made in two factories and ordering it from both is a legitimate thing to
// do — one line each, not one overwriting the other.
const lineKey = (productId, factoryId) => `${productId}:${factoryId}`;

export default function NewOrder() {
  const navigate = useNavigate();
  const { selectedShop } = useAuth();

  const [products, setProducts] = useState([]);
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Where we are in factory → category → products
  const [openFactoryId, setOpenFactoryId] = useState(null);
  const [openCategories, setOpenCategories] = useState({}); // { [catKey]: true }

  // Workers available at this shop (includes "Άγνωστος")
  const [workers, setWorkers] = useState([]);
  const [workerId, setWorkerId] = useState('');

  // { [`productId:factoryId`]: { quantity: string, notes: string } }
  const [cart, setCart] = useState({});

  // Customer fields
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  // Default requested time: now + 2 hours
  const [requestedFor, setRequestedFor] = useState(formatLocal(addHours(new Date(), 2)));

  const [submitting, setSubmitting] = useState(false);

  // Photos are held here until the order exists, then uploaded against its id
  const [photos, setPhotos] = useState([]); // [{ key, dataUrl, bytes }]
  const [preparing, setPreparing] = useState(false);

  const addPhotos = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = ''; // let the same file be picked again later
    if (files.length === 0) return;
    const room = MAX_IMAGES - photos.length;
    if (room <= 0) return toast.error(`Έως ${MAX_IMAGES} φωτογραφίες`);
    const take = files.slice(0, room);
    if (files.length > room) toast(`Προστέθηκαν ${room} από ${files.length}`);

    setPreparing(true);
    try {
      const prepared = [];
      for (const f of take) {
        if (!f.type.startsWith('image/')) continue;
        const dataUrl = await fileToResizedDataUrl(f);
        prepared.push({
          key: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
          dataUrl,
          bytes: dataUrlBytes(dataUrl),
        });
      }
      setPhotos((p) => [...p, ...prepared]);
    } catch (err) {
      toast.error('Σφάλμα επεξεργασίας εικόνας');
    } finally {
      setPreparing(false);
    }
  };

  const removePhoto = (key) => setPhotos((p) => p.filter((x) => x.key !== key));

  useEffect(() => {
    if (!selectedShop) return;
    setLoading(true);
    setWorkerId('');       // reset on shop change
    setOpenFactoryId(null);
    Promise.all([
      getProducts({ includeInactive: false, shopId: selectedShop.id }),
      getWorkersByShop(selectedShop.id),
      // shop-scoped: a factory hidden from this shop must not even appear
      getFactories(false, selectedShop.id),
    ])
      .then(([pRes, wRes, fRes]) => {
        setProducts(pRes.data);
        setWorkers(wRes.data);
        setFactories(fRes.data);
      })
      .catch((err) => toast.error(err.response?.data?.error || 'Σφάλμα φόρτωσης'))
      .finally(() => setLoading(false));
  }, [selectedShop]);

  // ── The menu tree ─────────────────────────────────────────
  // One entry per factory, each holding only the products that factory makes
  // and this shop may sell. A product made in two factories appears in both;
  // which one you walk in through is which one bakes it.
  //
  // The product list arrives already in the admin's drag order (category
  // first, then position inside it), so building the groups in order is
  // enough — nothing here re-sorts.
  const tree = useMemo(() => {
    const byFactory = new Map();
    for (const p of products) {
      for (const f of (p.factories || [])) {
        if (!byFactory.has(f.id)) byFactory.set(f.id, { id: f.id, name: f.name, cats: new Map(), count: 0 });
        const entry = byFactory.get(f.id);
        const catKey = p.category_id == null ? UNCAT_KEY : String(p.category_id);
        if (!entry.cats.has(catKey)) {
          entry.cats.set(catKey, {
            key: catKey,
            name: p.category_name || UNCAT_NAME,
            virtual: p.category_id == null,
            products: [],
          });
        }
        entry.cats.get(catKey).products.push(p);
        entry.count += 1;
      }
    }
    // Factory order follows the admin's own ordering, not the order products
    // happened to be read in. Factories with nothing for this shop drop out.
    const ordered = factories
      .map((f) => byFactory.get(f.id))
      .filter(Boolean);
    // Anything whose factory is missing from the list (inactive, say) still
    // belongs somewhere rather than silently disappearing.
    for (const [, entry] of byFactory) {
      if (!ordered.includes(entry)) ordered.push(entry);
    }
    return ordered.map((f) => {
      const cats = [...f.cats.values()];
      // "Χωρίς κατηγορία" always sits last
      cats.sort((a, b) => (a.virtual ? 1 : 0) - (b.virtual ? 1 : 0));
      return { ...f, cats };
    });
  }, [products, factories]);

  const openFactory = useMemo(
    () => tree.find((f) => f.id === openFactoryId) || null,
    [tree, openFactoryId]
  );

  // Searching skips the walk entirely: you get a flat list across every
  // factory, each row labelled, because someone who knows the product name
  // should not have to remember where it is baked.
  const searchHits = useMemo(() => {
    const q = normalize(search.trim());
    if (!q) return [];
    const hits = [];
    for (const p of products) {
      const match = normalize(p.name).includes(q)
        || (p.code && normalize(p.code).includes(q))
        || (p.category_name && normalize(p.category_name).includes(q));
      if (!match) continue;
      for (const f of (p.factories || [])) hits.push({ product: p, factory: f });
    }
    return hits;
  }, [products, search]);

  // ── Cart ──────────────────────────────────────────────────
  const parseQty = (v) => {
    if (v === '' || v === null || v === undefined) return 0;
    const n = parseFloat(String(v).replace(',', '.'));
    return isNaN(n) || n < 0 ? 0 : n;
  };

  const setQty = (productId, factoryId, value) => {
    const k = lineKey(productId, factoryId);
    setCart((c) => ({ ...c, [k]: { ...(c[k] || {}), quantity: value } }));
  };
  const setNotes = (productId, factoryId, notes) => {
    const k = lineKey(productId, factoryId);
    setCart((c) => ({ ...c, [k]: { ...(c[k] || {}), notes } }));
  };

  const cartItems = useMemo(() => {
    const items = [];
    const productById = new Map(products.map((p) => [p.id, p]));
    for (const [k, entry] of Object.entries(cart)) {
      const qty = parseQty(entry.quantity);
      if (qty <= 0) continue;
      const [pidStr, fidStr] = k.split(':');
      const p = productById.get(Number(pidStr));
      if (!p) continue;
      const fid = Number(fidStr);
      const f = (p.factories || []).find((x) => x.id === fid);
      items.push({
        key: k,
        product_id: p.id,
        product_name: p.name,
        unit_price: parseFloat(p.price),
        quantity: qty,
        factory_id: fid,
        factory_name: f ? f.name : '?',
        notes: entry.notes || '',
        total: parseFloat(p.price) * qty,
      });
    }
    return items;
  }, [products, cart]);

  const grandTotal = cartItems.reduce((s, it) => s + it.total, 0);

  // How many lines each factory has in the cart, for the badges on the tiles
  const countByFactory = useMemo(() => {
    const m = new Map();
    for (const it of cartItems) m.set(it.factory_id, (m.get(it.factory_id) || 0) + 1);
    return m;
  }, [cartItems]);

  const splitByFactory = useMemo(() => {
    const m = new Map();
    for (const it of cartItems) {
      if (!m.has(it.factory_id)) m.set(it.factory_id, { factory_id: it.factory_id, factory_name: it.factory_name, items: [], total: 0 });
      const g = m.get(it.factory_id);
      g.items.push(it);
      g.total += it.total;
    }
    return [...m.values()];
  }, [cartItems]);

  const submit = async () => {
    if (!selectedShop) return toast.error('Επιλέξτε κατάστημα');
    if (!workerId) return toast.error('Επιλέξτε εργαζόμενο');
    if (!requestedFor) return toast.error('Συμπληρώστε ημερομηνία/ώρα');
    if (cartItems.length === 0) return toast.error('Το καλάθι είναι άδειο');

    setSubmitting(true);
    try {
      const payload = {
        shop_id: selectedShop.id,
        created_by_worker_id: Number(workerId),
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim() || null,
        customer_notes: customerNotes.trim() || null,
        // Send the wall-clock time exactly as typed ("2026-10-05T15:00").
        // requested_for is a TIMESTAMP WITHOUT TIME ZONE and the backend's
        // Postgres sessions run on Europe/Athens, so converting to UTC here
        // would store the pickup time hours early.
        requested_for: requestedFor,
        items: cartItems.map((it) => ({
          product_id: it.product_id,
          factory_id: it.factory_id,
          quantity: it.quantity,
          notes: it.notes || null,
        })),
      };
      const res = await createOrder(payload);
      const created = res.data.order || (res.data.orders || [])[0];

      // Photos can only be attached once the order has an id, so they go up
      // immediately after. A failed photo doesn't undo the order — the order
      // is the important part — but we do say which ones didn't make it.
      if (created && photos.length > 0) {
        let failed = 0;
        for (const p of photos) {
          try {
            await uploadOrderImage(created.id, p.dataUrl);
          } catch (e) {
            failed += 1;
          }
        }
        if (failed > 0) {
          toast.error(`Η παραγγελία καταχωρήθηκε, αλλά ${failed} φωτογραφ${failed === 1 ? 'ία δεν ανέβηκε' : 'ίες δεν ανέβηκαν'}. Προσθέστε τες από τις Παραγγελίες μου.`);
        }
      }

      toast.success(created
        ? `Η παραγγελία #${created.id} καταχωρήθηκε!`
        : 'Η παραγγελία καταχωρήθηκε!');
      navigate('/my-orders');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα αποθήκευσης');
    } finally {
      setSubmitting(false);
    }
  };

  if (!selectedShop) {
    return (
      <>
        <div className="page-header">
          <h2>🛒 Νέα Παραγγελία</h2>
          <p>Δημιουργία νέας παραγγελίας από το κατάστημα</p>
        </div>
        <div className="page-body">
          <div className="card"><div className="card-body">
            <div className="empty-state">
              <div>🏪</div>
              <p>Δεν έχετε επιλεγμένο κατάστημα. Επικοινωνήστε με τον διαχειριστή.</p>
            </div>
          </div></div>
        </div>
      </>
    );
  }

  // ── One product row, used by both the walk and the search results ──
  const ProductRow = ({ p, factoryId, showWhere }) => {
    const k = lineKey(p.id, factoryId);
    const entry = cart[k] || {};
    const qty = parseQty(entry.quantity);
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 6,
        padding: '10px 12px',
        border: '1px solid var(--gray-100)', borderRadius: 8,
        background: qty > 0 ? '#f0f9ff' : 'white',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 500, color: 'var(--gray-800)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              {p.code && (
                <span style={{
                  fontSize: 11, fontWeight: 700, color: 'var(--gray-500)',
                  background: 'var(--gray-100)', borderRadius: 4, padding: '1px 6px',
                }}>{p.code}</span>
              )}
              {p.name}
            </div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
              €{parseFloat(p.price).toFixed(2)}
              {showWhere && <> · 🏭 {showWhere}</>}
            </div>
          </div>
          <input
            type="number"
            min="0"
            step="any"
            placeholder="0"
            value={entry.quantity ?? ''}
            onChange={(e) => setQty(p.id, factoryId, e.target.value)}
            className="form-input"
            style={{ width: 80, padding: '6px 10px', margin: 0, textAlign: 'right' }}
          />
          {qty > 0 && (
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--blue-dark)', minWidth: 70, textAlign: 'right' }}>
              €{(qty * parseFloat(p.price)).toFixed(2)}
            </div>
          )}
        </div>
        {qty > 0 && (
          <input
            placeholder="Σημείωση για αυτό το προϊόν (προαιρετικά)"
            value={entry.notes || ''}
            onChange={(e) => setNotes(p.id, factoryId, e.target.value)}
            className="form-input"
            style={{ padding: '4px 8px', fontSize: 12, margin: 0 }}
          />
        )}
      </div>
    );
  };

  const searching = !!search.trim();

  return (
    <>
      <div className="page-header">
        <div className="flex-between">
          <div>
            <h2>🛒 Νέα Παραγγελία</h2>
            <p>Κατάστημα: <strong>{selectedShop.name}</strong></p>
          </div>
        </div>
      </div>
      <div className="page-body">
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: 20, alignItems: 'start' }}>

          {/* LEFT: factory → category → products */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">
                {searching ? 'Αποτελέσματα' : (openFactory ? `🏭 ${openFactory.name}` : 'Εργοστάσια')}
              </span>
              <input
                className="form-input"
                placeholder="🔍 Αναζήτηση σε όλα..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: 220, padding: '6px 12px', margin: 0 }}
              />
            </div>
            <div className="card-body">
              {loading ? (
                <div className="page-loading"><span className="loading-spinner dark" /></div>

              ) : searching ? (
                /* Flat results, each labelled with where it is made */
                searchHits.length === 0 ? (
                  <div className="empty-state"><div>🥐</div><p>Δεν βρέθηκαν προϊόντα</p></div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {searchHits.map(({ product, factory }) => (
                      <ProductRow
                        key={lineKey(product.id, factory.id)}
                        p={product}
                        factoryId={factory.id}
                        showWhere={`${factory.name}${product.category_name ? ` · ${product.category_name}` : ''}`}
                      />
                    ))}
                  </div>
                )

              ) : !openFactory ? (
                /* Level 1 — pick a factory */
                tree.length === 0 ? (
                  <div className="empty-state"><div>🏭</div><p>Δεν υπάρχουν διαθέσιμα προϊόντα για αυτό το κατάστημα</p></div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10 }}>
                    {tree.map((f) => {
                      const inCart = countByFactory.get(f.id) || 0;
                      return (
                        <button
                          key={f.id}
                          onClick={() => { setOpenFactoryId(f.id); setOpenCategories({}); }}
                          style={{
                            textAlign: 'left', cursor: 'pointer', font: 'inherit',
                            padding: '16px 14px', borderRadius: 10,
                            border: `1px solid ${inCart ? 'var(--blue)' : 'var(--gray-200)'}`,
                            background: inCart ? '#f0f9ff' : 'white',
                            display: 'flex', flexDirection: 'column', gap: 4,
                          }}
                        >
                          <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--gray-900)' }}>
                            🏭 {f.name}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                            {f.cats.length} {f.cats.length === 1 ? 'κατηγορία' : 'κατηγορίες'} · {f.count} {f.count === 1 ? 'προϊόν' : 'προϊόντα'}
                          </div>
                          {inCart > 0 && (
                            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--blue-dark)' }}>
                              🛒 {inCart} στο καλάθι
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )

              ) : (
                /* Level 2 — the factory's categories, opening in place */
                <>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setOpenFactoryId(null)}
                    style={{ marginBottom: 12 }}
                  >← Όλα τα εργοστάσια</button>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {openFactory.cats.map((c) => {
                      const isOpen = !!openCategories[c.key];
                      const inCart = c.products.filter(
                        (p) => parseQty((cart[lineKey(p.id, openFactory.id)] || {}).quantity) > 0
                      ).length;
                      return (
                        <div key={c.key}>
                          <button
                            onClick={() => setOpenCategories((o) => ({ ...o, [c.key]: !o[c.key] }))}
                            style={{
                              width: '100%', textAlign: 'left', cursor: 'pointer', font: 'inherit',
                              display: 'flex', alignItems: 'center', gap: 10,
                              padding: '12px 14px', borderRadius: 8,
                              border: '1px solid var(--gray-200)',
                              background: isOpen ? 'var(--gray-100)' : 'white',
                            }}
                          >
                            <span style={{ color: 'var(--gray-400)', fontSize: 13 }}>{isOpen ? '▾' : '▸'}</span>
                            <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--gray-900)', flex: 1 }}>
                              {c.virtual ? '📦 ' : '🗂 '}{c.name}
                            </span>
                            {inCart > 0 && (
                              <span className="badge badge-blue">🛒 {inCart}</span>
                            )}
                            <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                              {c.products.length}
                            </span>
                          </button>

                          {isOpen && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 0 4px 0' }}>
                              {c.products.map((p) => (
                                <ProductRow key={lineKey(p.id, openFactory.id)} p={p} factoryId={openFactory.id} />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* RIGHT: customer info + summary */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, position: 'sticky', top: 16 }}>
            <div className="card">
              <div className="card-header"><span className="card-title">Στοιχεία Πελάτη</span></div>
              <div className="card-body">
                <div className="form-group">
                  <label className="form-label">Παραγγέλθηκε από *</label>
                  <select className="form-select" value={workerId} onChange={(e) => setWorkerId(e.target.value)}>
                    <option value="">-- Επιλέξτε εργαζόμενο --</option>
                    {workers.map((w) => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Όνομα πελάτη</label>
                  <input className="form-input" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Τηλέφωνο</label>
                  <input className="form-input" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Ημερομηνία/ώρα παραλαβής *</label>
                  <input type="datetime-local" className="form-input" value={requestedFor} onChange={(e) => setRequestedFor(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Σημειώσεις</label>
                  <textarea className="form-textarea" rows="2" value={customerNotes} onChange={(e) => setCustomerNotes(e.target.value)} />
                </div>

                {/* Photos. On a tablet this button offers the camera or the
                    photo library; on a computer it's the file picker. */}
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">
                    Φωτογραφίες ({photos.length}/{MAX_IMAGES})
                  </label>
                  {photos.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                      {photos.map((p) => (
                        <div key={p.key} style={{ position: 'relative' }}>
                          <img
                            src={p.dataUrl}
                            alt=""
                            style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--gray-200)', display: 'block' }}
                          />
                          <button
                            type="button"
                            onClick={() => removePhoto(p.key)}
                            title="Αφαίρεση"
                            style={{
                              position: 'absolute', top: -6, right: -6,
                              width: 22, height: 22, borderRadius: '50%',
                              border: 'none', background: 'var(--red, #ef4444)', color: 'white',
                              cursor: 'pointer', fontSize: 13, lineHeight: '22px', padding: 0,
                            }}
                          >×</button>
                          <div style={{ fontSize: 10, color: 'var(--gray-400)', textAlign: 'center', marginTop: 2 }}>
                            {prettyBytes(p.bytes)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <label
                    className="btn btn-secondary btn-block"
                    style={{ cursor: photos.length >= MAX_IMAGES ? 'not-allowed' : 'pointer', opacity: photos.length >= MAX_IMAGES ? 0.5 : 1, textAlign: 'center' }}
                  >
                    {preparing ? 'Επεξεργασία...' : '📷 Προσθήκη φωτογραφίας'}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      disabled={preparing || photos.length >= MAX_IMAGES}
                      onChange={addPhotos}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 6 }}>
                    Οι φωτογραφίες σμικρύνονται αυτόματα και διαγράφονται μία εβδομάδα μετά την ολοκλήρωση.
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header"><span className="card-title">Σύνοψη</span></div>
              <div className="card-body" style={{ padding: '14px 16px' }}>
                {cartItems.length === 0 ? (
                  <div style={{ fontSize: 13, color: 'var(--gray-400)', textAlign: 'center', padding: 12 }}>Άδειο καλάθι</div>
                ) : (
                  <>
                    {splitByFactory.map((g, idx) => (
                      <div key={g.factory_id} style={{
                        padding: '8px 0',
                        borderBottom: idx < splitByFactory.length - 1 ? '1px solid var(--gray-100)' : 'none',
                      }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-700)', marginBottom: 4 }}>
                          🏭 {g.factory_name}
                        </div>
                        {g.items.map((it) => (
                          <div key={it.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--gray-600)', padding: '2px 0' }}>
                            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {it.product_name} × {it.quantity.toLocaleString('el-GR', { maximumFractionDigits: 3 })}
                            </span>
                            <span style={{ marginLeft: 8 }}>€{it.total.toFixed(2)}</span>
                          </div>
                        ))}
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 600, color: 'var(--gray-800)', marginTop: 4 }}>
                          <span>Σύνολο</span>
                          <span>€{g.total.toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                    {splitByFactory.length > 1 && (
                      <div style={{ fontSize: 11, color: 'var(--blue-dark)', padding: '6px 8px', textAlign: 'center', background: 'var(--blue-light)', borderRadius: 6, marginTop: 6 }}>
                        ℹ️ Μία παραγγελία, {splitByFactory.length} εργοστάσια. Κάθε εργοστάσιο βλέπει μόνο
                        τα δικά του προϊόντα και η παραγγελία γίνεται Έτοιμη όταν τελειώσουν όλα.
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 700, color: 'var(--blue-dark)', borderTop: '2px solid var(--gray-200)', marginTop: 8, paddingTop: 8 }}>
                      <span>ΣΥΝΟΛΟ</span>
                      <span>€{grandTotal.toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <button
              className="btn btn-primary btn-block"
              style={{ padding: '12px 16px', fontSize: 15 }}
              disabled={submitting || cartItems.length === 0 || !requestedFor || !workerId}
              onClick={submit}
            >
              {submitting ? <span className="loading-spinner" /> : '💾 Καταχώρηση Παραγγελίας'}
            </button>
          </div>

        </div>
      </div>
    </>
  );
}
